import { DS_SHAPE_SPACING, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { MediaPack } from '@calm/evexia-web-sdk';
import { CardVariant } from '@rhg/components/card/types';
import {
    defaultCarouselCardSpacing,
    defaultCarouselCardWidth,
    defaultCarouselCardWidthMobile,
} from '@rhg/components/carousel/utils';
import ChLink from '@rhg/components/ch-link';
import { dailyPracticeCardWidth } from '@rhg/components/discover-dailies/daily-packs';
import { ViewAllCategoryButton } from '@rhg/components/discover/view-all-category-button';
import { PackCarousel, PackCarouselProps } from '@rhg/components/pack-carousel';
import { SkeletonCardVariant } from '@rhg/components/ui/skeletons';
import { useActiveCircleId } from '@rhg/hooks/use-active-circle';
import { useCurrentBreakpointName } from '@rhg/hooks/use-current-breakpoint-name';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePacks } from '@rhg/hooks/use-packs';
import commonMessages from '@rhg/lang/messages/common';
import { Box, Button, Divider, Typography } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { getCardVariantFromDisplayType } from '@rhg/utils/pack-card';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { styleCardFooter } from '..';
import {
    PackDetailsFullWidthContainer,
    PackDetailsPaddedContainer,
    PackDetailsPaddingPx,
} from '../layout';

export const largeButtonCardWidth = 209;
export const mobileCategoryCardWidth = 120;
export const MAX_CARDS_DISPLAYED = 5;

export interface PackCarouselSectionProps {
    packClass?: string;
    title?: string;
    titleSrOnly?: boolean;
    showDivider?: boolean;
    viewAllHref?: string;
    cardVariant?: CardVariant;
    skeletonType?: SkeletonCardVariant;
    cardWidth?: PackCarouselProps['itemWidth'];
    packCarouselProps?: Partial<PackCarouselProps>;
}

export const styleTitleRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: DS_SPACING_UNITS.HEADING_CONTENT_DESKTOP,
};

const styleMobileCarouselException = {
    gridTemplateRows: '1fr 1fr',
    gridAutoFlow: 'column',
    pr: `${DS_SHAPE_SPACING.SIDE_SMALL}px`,
    ':before': {
        content: 'none',
    },
};

const stylePackCarouselTitle = {
    mb: 2,
};

const styleSectionReponsiveDivider = {
    mt: DS_SPACING_UNITS.SECTION,
    mb: { xs: DS_SPACING_UNITS.CONTENT, md: DS_SPACING_UNITS.SECTION },
};

export const PackCarouselSection = ({
    packClass,
    title,
    titleSrOnly,
    showDivider = true,
    viewAllHref,
    cardWidth,
    cardVariant: providedCardVariant = 'carousel',
    skeletonType = 'daily',
    packCarouselProps,
}: PackCarouselSectionProps) => {
    const currentBreakpoint = useCurrentBreakpointName();
    const { activeCircleId } = useActiveCircleId();
    const { isFetched, packs } = usePacks([packClass ?? ''], activeCircleId);
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();

    const ogPack = packs?.[0];
    const isQuickNavCategory =
        ogPack?.displayType === 'calm-health-snappy-quick-nav';
    const isMobileQuickNav = isMobile && isQuickNavCategory;
    const skipMaxCardLimit =
        isQuickNavCategory ||
        ogPack?.displayType === 'calm-health-goals-row-cards';
    const pack = useMemo(
        () =>
            skipMaxCardLimit
                ? ogPack
                : ({
                      ...ogPack,
                      items: ogPack?.items?.slice(0, MAX_CARDS_DISPLAYED),
                  } as MediaPack),
        [ogPack, skipMaxCardLimit]
    );

    const isViewAllLink = pack?.displayType === 'link';
    if (isViewAllLink) {
        return (
            <ViewAllCategoryButton
                label={title ?? ''}
                packClass={packClass ?? ''}
            />
        );
    }

    const isFeatured = pack?.displayType === 'banner-carousel';
    const isGoalCards = pack?.displayType === 'calm-health-goals-row-cards';
    const cardVariant = isFeatured
        ? getCardVariantFromDisplayType(pack?.displayType)
        : providedCardVariant;
    const cardWidthFallback = isGoalCards
        ? largeButtonCardWidth
        : isFeatured
        ? isMobile
            ? defaultCarouselCardWidthMobile
            : defaultCarouselCardWidth
        : dailyPracticeCardWidth;

    let itemWidth = cardWidth ?? cardWidthFallback;
    if (isMobile && skipMaxCardLimit) {
        itemWidth = isQuickNavCategory
            ? mobileCategoryCardWidth
            : cardWidthFallback;
    }
    const displayTitle = title && !titleSrOnly;

    return (
        <>
            {(displayTitle || viewAllHref) && (
                <PackDetailsPaddedContainer>
                    <Box sx={styleTitleRow}>
                        {displayTitle && (
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={stylePackCarouselTitle}
                            >
                                {title}
                            </Typography>
                        )}
                        {viewAllHref && (
                            <ChLink href={viewAllHref} passHref legacyBehavior>
                                <Button
                                    trackButton="ViewAllCategory"
                                    color="primary"
                                    size="medium"
                                    variant="text"
                                >
                                    {formatMessage(
                                        commonMessages['common.view_all']
                                    )}
                                </Button>
                            </ChLink>
                        )}
                    </Box>
                </PackDetailsPaddedContainer>
            )}
            {title && titleSrOnly && (
                <Typography variant="srOnly" component="h2">
                    {title}
                </Typography>
            )}
            <PackDetailsFullWidthContainer>
                <PackCarousel
                    isFullscreen
                    data-testid="PackCarousel"
                    pack={pack}
                    cardVariant={cardVariant}
                    itemWidth={itemWidth}
                    itemSpacing={
                        isMobileQuickNav
                            ? DS_SHAPE_SPACING.SIDE_SMALL
                            : defaultCarouselCardSpacing
                    }
                    firstItemOffsetPx={PackDetailsPaddingPx}
                    trackBackButton="DiscoverCarouselBack"
                    trackForwardButton="DiscoverCarouselForward"
                    cardContentSx={styleCardFooter}
                    isFetched={isFetched}
                    skeletonType={skeletonType}
                    allowPackOfPacks
                    {...packCarouselProps}
                    listSx={sxAppend(
                        isQuickNavCategory && currentBreakpoint === 'xl'
                            ? {
                                  '&.MuiList-root.MuiList-root': {
                                      maxWidth: 'max-content',
                                  },
                              }
                            : {},
                        packCarouselProps?.listSx
                    )}
                    sx={sxAppend(
                        skipMaxCardLimit && isMobile
                            ? {
                                  '& .MuiList-root': {
                                      ...styleMobileCarouselException,
                                      gridTemplateColumns: `repeat(${Math.ceil(
                                          pack?.items?.length / 2
                                      )}, minmax(${
                                          isQuickNavCategory
                                              ? mobileCategoryCardWidth
                                              : cardWidthFallback
                                      }px, 1fr))`,
                                      pl: isQuickNavCategory
                                          ? `${DS_SHAPE_SPACING.SIDE_SMALL}px`
                                          : PackDetailsPaddingPx,
                                  },
                              }
                            : {},
                        packCarouselProps?.sx
                    )}
                />
            </PackDetailsFullWidthContainer>
            {showDivider && (
                <PackDetailsPaddedContainer>
                    <Divider sx={styleSectionReponsiveDivider} />
                </PackDetailsPaddedContainer>
            )}
        </>
    );
};
